import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2, MessagesSquare, MinusCircle, Plus, PlusCircle, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/misc'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import { cn, initials } from '@/lib/utils'
import { useCreateTag, useTags, useUpdateTag, type TagDef } from '@/hooks/use-settings'
import { automationKeys, useAutomationRules, useCreateRule, useDeleteRule, useUpdateRule } from '@/hooks/use-automation'
import { useConversations } from '@/hooks/use-conversations'
import {
  TAG_RULE_FIELDS,
  TAG_RULE_OPS,
  draftToFlowConfig,
  emptyCondition,
  emptyTagRule,
  isTagRule,
  opNeedsValue,
  ruleName,
  ruleToDraft,
  validateDraft,
  type TagRuleDraft,
} from './tag-rules'
import { ZaloLabelImportButton } from './zalo-label-import-dialog'

const PRESET_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#f97316', '#ec4899', '#ef4444']

// ─────────────────────────────────────────────────────────────────────────────
// Thêm / sửa nhãn
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Thêm nhãn mới (`tag` = null) hoặc sửa nhãn. Đổi tên nhãn thì cập nhật luôn
 * các quy tắc tự gắn đang trỏ tới tên cũ — nhãn lưu theo TÊN trên hồ sơ khách.
 */
export function TagEditorDialog({
  open,
  onOpenChange,
  tag,
  onSaved,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  tag: TagDef | null
  /** Sau khi tạo mới — vd mở luôn hộp quy tắc cho nhãn vừa tạo. */
  onSaved?: (tag: TagDef) => void
}) {
  const { t } = useTranslation('convContact')
  const createTag = useCreateTag()
  const updateTag = useUpdateTag()
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [color, setColor] = useState(PRESET_COLORS[0])

  useEffect(() => {
    if (!open) return
    setName(tag?.name ?? '')
    setColor(tag?.color ?? PRESET_COLORS[0])
  }, [open, tag])

  const busy = createTag.isPending || updateTag.isPending

  async function save() {
    const n = name.trim()
    if (!n) return toast.error(t('tagEditor.needName'))
    try {
      if (tag) {
        const saved = await updateTag.mutateAsync({ id: tag.id, name: n, color })
        // Đổi tên → máy chủ tự đổi theo trên hồ sơ khách và các luồng / quy tắc tự gắn nhãn (PUT /tags/:id) — chỉ cần
        // quyền sửa nhãn, không cần quyền Tự động hoá. Nạp lại danh sách quy tắc cho khớp.
        if (n !== tag.name) void qc.invalidateQueries({ queryKey: automationKeys.rules })
        toast.success(t('tagEditor.updated'))
        onSaved?.(saved)
      } else {
        const saved = await createTag.mutateAsync({ name: n, color })
        toast.success(t('tagEditor.created', { name: saved.name }))
        onSaved?.(saved)
      }
      onOpenChange(false)
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{tag ? t('tagEditor.titleEdit') : t('tagEditor.titleNew')}</DialogTitle>
          <DialogDescription>{t('tagEditor.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="tag-name">{t('tagEditor.nameLabel')}</Label>
            <Input
              id="tag-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && save()}
              placeholder={t('tagEditor.namePlaceholder')}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t('tagEditor.color')}</Label>
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={t('tagEditor.colorAria', { color: c })}
                  onClick={() => setColor(c)}
                  className={cn('h-7 w-7 rounded-full ring-offset-2 transition', color === c && 'ring-2 ring-ring')}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          <span
            className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
            style={{ backgroundColor: `${color}20`, color }}
          >
            {name.trim() || t('tagEditor.preview')}
          </span>
          {/* Công cụ: kéo nhãn đã gắn ở Zalo PC về (chỉ khi thêm nhãn mới). */}
          {!tag && <ZaloLabelImportButton />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('tagEditor.cancel')}
          </Button>
          <Button onClick={save} disabled={busy}>
            {busy && <Loader2 className="animate-spin" />}
            {t('common:actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Quy tắc tự gắn nhãn (mẫu Lark "Thêm quy tắc nhãn")
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Danh sách quy tắc "Nếu [tất cả/bất kỳ] điều kiện … thì Thêm vào nhãn …".
 * `tag` = mở cho một nhãn (chỉ hiện quy tắc của nhãn đó, quy tắc mới mặc định
 * gắn nhãn này); null = mọi quy tắc nhãn. Lưu = đồng bộ xuống Tự động hoá
 * (tạo / sửa / xoá luồng tương ứng).
 */
export function TagRulesDialog({
  open,
  onOpenChange,
  tag,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  tag: TagDef | null
}) {
  const { t } = useTranslation('convContact')
  const { data: tags = [] } = useTags()
  // Chỉ tải khi hộp đang mở — hộp luôn được gắn sẵn trong cây giao diện, người không có quyền Tự động hoá không mở được.
  const { data: rulesData, isLoading, isError } = useAutomationRules(open)
  const createRule = useCreateRule()
  const updateRule = useUpdateRule()
  const deleteRule = useDeleteRule()
  const [drafts, setDrafts] = useState<TagRuleDraft[]>([])
  const [removed, setRemoved] = useState<string[]>([])
  const [active, setActive] = useState(0)
  const [saving, setSaving] = useState(false)

  const existing = useMemo(
    () =>
      (rulesData?.rules ?? [])
        .filter(isTagRule)
        .map(ruleToDraft)
        .filter((d): d is TagRuleDraft => !!d && (!tag || d.tag === tag.name)),
    [rulesData, tag],
  )

  // Mở lại → nạp lại từ server (bỏ thay đổi chưa lưu).
  useEffect(() => {
    if (!open || isLoading) return
    setDrafts(existing.length ? existing : [emptyTagRule(tag?.name ?? tags[0]?.name ?? '')])
    setRemoved([])
    setActive(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isLoading])

  const patch = (i: number, p: Partial<TagRuleDraft>) =>
    setDrafts((ds) => ds.map((d, j) => (j === i ? { ...d, ...p } : d)))
  const patchCond = (i: number, k: number, p: Partial<TagRuleDraft['conditions'][number]>) =>
    setDrafts((ds) =>
      ds.map((d, j) => (j === i ? { ...d, conditions: d.conditions.map((c, m) => (m === k ? { ...c, ...p } : c)) } : d)),
    )
  const removeRule = (i: number) => {
    const d = drafts[i]
    if (d.ruleId) setRemoved((r) => [...r, d.ruleId!])
    setDrafts((ds) => ds.filter((_, j) => j !== i))
    setActive(0)
  }

  async function save() {
    for (const [i, d] of drafts.entries()) {
      const err = validateDraft(d)
      if (err) {
        setActive(i)
        return toast.error(t('tagRulesDlg.ruleError', { n: i + 1, err }))
      }
    }
    setSaving(true)
    try {
      for (const id of removed) await deleteRule.mutateAsync(id)
      for (const d of drafts) {
        const body = { name: ruleName(d.tag), trigger: 'message_received', enabled: d.enabled, flowVersion: 2, flowConfig: draftToFlowConfig(d) }
        if (d.ruleId) await updateRule.mutateAsync({ id: d.ruleId, data: body })
        else await createRule.mutateAsync({ ...body, description: 'Quy tắc tự gắn nhãn — tạo từ Nhắn tin → Nhãn' }) // i18n-ignore
      }
      toast.success(t('tagRulesDlg.saved'))
      onOpenChange(false)
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setSaving(false)
    }
  }

  const current = drafts[active]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>{tag ? t('tagRulesDlg.titleFor', { name: tag.name }) : t('tagRulesDlg.title')}</DialogTitle>
          <DialogDescription>
            {t('tagRulesDlg.description')}
          </DialogDescription>
        </DialogHeader>

        {isError ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            {t('tagRulesDlg.noPermission')}
          </p>
        ) : (
          <div className="grid min-h-[420px] gap-6 lg:grid-cols-[1fr_340px]">
            <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
              {drafts.map((d, i) => (
                <div
                  key={d.ruleId ?? `new-${i}`}
                  onClick={() => setActive(i)}
                  className={cn(
                    'space-y-3 rounded-xl border p-4 transition-colors',
                    i === active ? 'border-primary/50 bg-primary/[0.02]' : 'hover:border-primary/30',
                  )}
                >
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span>{t('tagRulesDlg.if')}</span>
                    <Select value={d.logic} onValueChange={(v) => patch(i, { logic: v as TagRuleDraft['logic'] })}>
                      <SelectTrigger className="w-52">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="and">{t('tagRulesDlg.logicAnd')}</SelectItem>
                        <SelectItem value="or">{t('tagRulesDlg.logicOr')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="ml-auto flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{d.enabled ? t('tagRulesDlg.on') : t('tagRulesDlg.off')}</span>
                      <Switch checked={d.enabled} onCheckedChange={(v) => patch(i, { enabled: v })} aria-label={t('tagRulesDlg.toggleAria')} />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation()
                          removeRule(i)
                        }}
                        aria-label={t('tagRulesDlg.deleteAria')}
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2 rounded-lg bg-muted/60 p-3">
                    {d.conditions.map((c, k) => (
                      <div key={k} className="flex items-center gap-2">
                        <Select value={c.field} onValueChange={(v) => patchCond(i, k, { field: v })}>
                          <SelectTrigger className="w-44 shrink-0">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TAG_RULE_FIELDS.map((f) => (
                              <SelectItem key={f.value} value={f.value}>
                                {f.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select value={c.op} onValueChange={(v) => patchCond(i, k, { op: v })}>
                          <SelectTrigger className="w-32 shrink-0">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TAG_RULE_OPS.map((o) => (
                              <SelectItem key={o.value} value={o.value}>
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {opNeedsValue(c.op) ? (
                          <Input
                            value={c.value}
                            onChange={(e) => patchCond(i, k, { value: e.target.value })}
                            placeholder={t('tagRulesDlg.keywordPlaceholder')}
                            className="min-w-0 flex-1"
                          />
                        ) : (
                          <span className="flex-1" />
                        )}
                        <button
                          type="button"
                          aria-label={t('tagRulesDlg.removeCondition')}
                          disabled={d.conditions.length === 1}
                          onClick={() => patch(i, { conditions: d.conditions.filter((_, m) => m !== k) })}
                          className="text-muted-foreground hover:text-destructive disabled:opacity-30"
                        >
                          <MinusCircle className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          aria-label={t('tagRulesDlg.addCondition')}
                          onClick={() => {
                            const next = [...d.conditions]
                            next.splice(k + 1, 0, emptyCondition())
                            patch(i, { conditions: next })
                          }}
                          className="text-muted-foreground hover:text-primary"
                        >
                          <PlusCircle className="h-5 w-5" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <p className="text-sm">{t('tagRulesDlg.thenDo')}</p>
                  <div className="flex flex-wrap items-center gap-2 rounded-lg bg-muted/60 p-3 text-sm">
                    <span>{t('tagRulesDlg.addToTag')}</span>
                    <Select value={d.tag} onValueChange={(v) => patch(i, { tag: v })}>
                      <SelectTrigger className="w-56">
                        <SelectValue placeholder={t('tagRulesDlg.pickTag')} />
                      </SelectTrigger>
                      <SelectContent>
                        {tags.map((tg) => (
                          <SelectItem key={tg.id} value={tg.name}>
                            <span className="flex items-center gap-2">
                              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: tg.color }} />
                              {tg.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={() => {
                  setDrafts((ds) => [...ds, emptyTagRule(tag?.name ?? tags[0]?.name ?? '')])
                  setActive(drafts.length)
                }}
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
              >
                <Plus className="h-4 w-4" /> {t('tagRulesDlg.addRule')}
              </button>
            </div>

            <RulePreview draft={current} />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('tagEditor.cancel')}
          </Button>
          <Button onClick={save} disabled={saving || isError || isLoading}>
            {saving && <Loader2 className="animate-spin" />}
            {t('common:actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Cột phải: hội thoại hiện có khớp điều kiện tên/SĐT của quy tắc đang chọn.
 * Điều kiện theo nội dung tin nhắn chỉ xét được khi tin mới tới — nói rõ thay
 * vì hiện danh sách sai.
 */
function RulePreview({ draft }: { draft?: TagRuleDraft }) {
  const { t } = useTranslation('convContact')
  const probe = draft?.conditions.find(
    (c) => (c.field === 'contact.fullName' || c.field === 'contact.phone') && c.op === 'contains' && c.value.trim().length >= 2,
  )
  const { data, isFetching } = useConversations({ search: probe?.value.trim(), limit: 20 }, !!probe)
  const items = probe ? data?.conversations ?? [] : []

  return (
    <div className="flex min-h-0 flex-col border-t pt-4 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
      <p className="text-sm font-medium">{t('rulePreview.title')}</p>
      <p className="mb-3 text-xs text-muted-foreground">
        {probe
          ? t('rulePreview.byCondition', {
              field: probe.field === 'contact.phone' ? t('rulePreview.fieldPhone') : t('rulePreview.fieldName'),
              value: probe.value.trim(),
            })
          : t('rulePreview.hint')}
      </p>
      {probe && isFetching ? (
        <Loader2 className="mx-auto mt-10 h-5 w-5 animate-spin text-muted-foreground" />
      ) : items.length ? (
        <ul className="max-h-[50vh] space-y-1 overflow-y-auto">
          {items.map((c) => (
            <li key={c.id} className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-muted/60">
              <Avatar className="h-8 w-8">
                {c.contact?.avatarUrl && <AvatarImage src={c.contact.avatarUrl} alt="" />}
                <AvatarFallback className="text-xs">{initials(c.contact?.fullName || c.displayName || '?')}</AvatarFallback>
              </Avatar>
              <span className="min-w-0 truncate text-sm">{c.contact?.fullName || c.displayName || t('rulePreview.noName')}</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center py-10 text-center text-sm text-muted-foreground">
          <MessagesSquare className="mb-2 h-10 w-10 opacity-40" />
          {t('rulePreview.none')}
        </div>
      )}
      <p className="mt-3 text-[11px] text-muted-foreground">
        {t('rulePreview.footer')}
      </p>
    </div>
  )
}
